/*
 * EINE BUEHNE ANHEFTEN (Rafael, 05.10.2026 19:45Z: „ein Thema im Vordergrund halten und
 * trotzdem woanders weiterarbeiten — dann sind die Browser halt alle im Vordergrund").
 *
 * Ohne Anheften montiert der LayoutManager genau EINE Buehne (die gerade offene). Wer die
 * Buehne wechselt, baut die alte ab: ihre Webviews laden beim Zurueckkommen neu, alles
 * Eingetragene ist weg, und `/api/browser-panel/<id>/eval` erreicht sie nicht mehr.
 *
 * Eine angeheftete Buehne bleibt montiert — versteckt mit visibility:hidden (wie die
 * Arbeitsbereiche in App.tsx; display:none wuerde die Webviews anhalten). Gemerkt wird je
 * GERAET (localStorage), hoechstens EINE Buehne: bis zu ~8 Webviews auf Mac/iPad.
 *
 * Gespeichert wird ein Schnappschuss des Dokuments, damit die Buehne nach einem Neuladen
 * der CUI wieder (versteckt) montiert werden kann. Oeffnet Rafael sie normal, ersetzt das
 * frische Dokument den Schnappschuss. GRENZE: ein Neuladen der CUI oder ein anderes Geraet
 * verliert die Eingaben trotzdem — dafuer ist die Vorbefuellung (Kachel-Rezept) da.
 */
import { useSyncExternalStore } from 'react';
import { alleTabs, type BuehnenBezug } from '../../shared/buehnen-anordnung';
import type { IJsonModel } from 'flexlayout-react';

export interface AngehefteteBuehne {
  id: string;
  /** Der Arbeitsbereich, in dessen LayoutManager sie montiert ist — beim Anheften eingefroren. */
  projectId: string;
  doc: unknown;
  bezug: BuehnenBezug;
  seit: number;
  /**
   * Gehalten per API (routes/buehne-halten.ts): `server`. Dann raeumt die App den Halt
   * auch wieder ab, sobald der Server ihn freigibt. Ein Knopf-Halt ohne Server-Wissen
   * (vor dem 06.10.2026 gesetzt) bleibt unangetastet.
   */
  quelle?: 'server';
  /**
   * Die Abloesungskette ab `id` (`nachfolger`, vom Server aufgeloest). Jede dieser
   * Buehnen IST die angeheftete: dieselben Kachel-Kennungen, dieselben montierten
   * Fenster. Ohne die Kette stuende die Folgebuehne als zweite Buehne daneben —
   * Kennungskollision, und wer sie oeffnet, saehe frisch geladene, leere Seiten.
   */
  kette?: string[];
}

const PRAEFIX = 'cui-buehne-angeheftet:v1:';
const schluessel = (nutzer: string) => PRAEFIX + nutzer;
const zuhoerer = new Set<() => void>();
const cache = new Map<string, AngehefteteBuehne | null>();

function gueltig(x: unknown): x is AngehefteteBuehne {
  const o = x as AngehefteteBuehne | null;
  return !!o && typeof o.id === 'string' && !!o.id && typeof o.projectId === 'string' && !!o.projectId
    && !!o.doc && typeof o.doc === 'object' && !!o.bezug && typeof o.bezug.id === 'string'
    && (o.kette === undefined || (Array.isArray(o.kette) && o.kette.every(k => typeof k === 'string')));
}

/** Ist `id` die angeheftete Buehne selbst oder eine ihrer Nachfolgerinnen? */
export function gehoertZurAngehefteten(angeheftet: AngehefteteBuehne | null, id: string | null | undefined): boolean {
  if (!angeheftet || !id) return false;
  return angeheftet.id === id || !!angeheftet.kette?.includes(id);
}

export function leseAngeheftet(nutzer: string): AngehefteteBuehne | null {
  if (cache.has(nutzer)) return cache.get(nutzer)!;
  let wert: AngehefteteBuehne | null = null;
  try {
    const roh = localStorage.getItem(schluessel(nutzer));
    if (roh) {
      const p = JSON.parse(roh);
      if (gueltig(p)) wert = p;
      else console.error('[buehnenAnheften] gespeicherter Eintrag unbrauchbar, verworfen:', roh.slice(0, 200));
    }
  } catch (e) { console.error('[buehnenAnheften] localStorage nicht lesbar:', e); }
  cache.set(nutzer, wert);
  return wert;
}

function melden() { for (const f of zuhoerer) f(); }

/** Wirft, wenn der Geraetespeicher voll/gesperrt ist — der Knopf zeigt das an. */
export function setzeAngeheftet(nutzer: string, wert: AngehefteteBuehne | null): void {
  if (wert && !gueltig(wert)) throw new Error('Angeheftete Bühne unvollständig (id/projectId/doc/bezug).');
  if (wert) localStorage.setItem(schluessel(nutzer), JSON.stringify(wert));
  else localStorage.removeItem(schluessel(nutzer));
  cache.set(nutzer, wert);
  melden();
}

if (typeof window !== 'undefined') {
  // Ein zweites Fenster desselben Geraets soll dieselbe Wahl sehen.
  window.addEventListener('storage', e => {
    if (!e.key?.startsWith(PRAEFIX)) return;
    cache.delete(e.key.slice(PRAEFIX.length));
    melden();
  });
}

function abonnieren(f: () => void) { zuhoerer.add(f); return () => { zuhoerer.delete(f); }; }

export function useAngeheftet(nutzer: string): AngehefteteBuehne | null {
  return useSyncExternalStore(abonnieren, () => leseAngeheftet(nutzer), () => null);
}

/** Nur die Kennung — fuer Markierungen in Listen, die das Dokument nicht brauchen. */
export function useAngeheftetId(nutzer: string): string | null {
  return useSyncExternalStore(abonnieren, () => leseAngeheftet(nutzer)?.id ?? null, () => null);
}

/**
 * Fensterkennungen, die in BEIDEN Dokumenten stehen. Panel-Kennungen sind global
 * (Fernsteuerung, Konsole, `data-node-id`) — zwei gleichzeitig montierte Buehnen mit
 * derselben Kennung wuerden beide auf jeden `eval` antworten. Kachel-Kennungen tragen
 * Art, Platz und einen Abdruck des Inhalts (shared/guide-kachel-id.ts), Treffer sind
 * selten, aber moeglich (gleiche Seite an gleicher Stelle).
 */
export function gemeinsameFenster(a: unknown, b: unknown): string[] {
  if (!a || !b) return [];
  const ids = new Set(alleTabs(a as IJsonModel).map(t => String(t.id)));
  return alleTabs(b as IJsonModel).map(t => String(t.id)).filter(id => ids.has(id));
}

export interface BuehnenEintrag {
  id: string; doc: unknown; bezug?: BuehnenBezug;
  sichtbar: boolean; angeheftet: boolean;
  /** Nur bei der angehefteten, gerade gezeigten: das frische Dokument weicht vom Schnappschuss ab. */
  veraltet?: boolean;
  /**
   * Nur beim sichtbaren Eintrag: welche Buehne er ZEIGT. Bei einer Nachfolgerin der angehefteten
   * ist das nicht `id` (der Schluessel bleibt die angeheftete). Der Oeffnungsbeleg vergleicht
   * hiergegen (`data-buehne-zeigt`, ASSIOEFFNENR R2 P2-a).
   */
  zeigt?: string;
}

/**
 * Welche Buehnen ein LayoutManager montiert. Reihenfolge ist egal (React-Schluessel = id),
 * wichtig ist: dieselbe Buehne steht nie zweimal da, und die angeheftete bleibt in der
 * Liste, auch wenn sie nicht gezeigt wird.
 *
 * - `aktuell`: die gerade gezeigte Buehne dieses Arbeitsbereichs (oder null).
 * - `angeheftet`: die angeheftete, sofern SIE in diesem Arbeitsbereich wohnt.
 *
 * Ist die aktuelle zugleich die angeheftete, bleibt der SCHNAPPSCHUSS montiert, nicht das
 * frisch gebaute Dokument: jede Abweichung (Rezept geaendert, Beraterchat anders angehaengt)
 * aendert den Abdruck und baut die Fenster neu — genau das, wovor Anheften schuetzen soll.
 * Die Abweichung wird stattdessen als `veraltet` gemeldet und an der Buehne angezeigt;
 * Loesen + erneut Oeffnen holt die neue Fassung.
 */
export function buehnenStapel(
  aktuell: { doc: unknown; bezug?: BuehnenBezug } | null,
  angeheftet: AngehefteteBuehne | null,
): { eintraege: BuehnenEintrag[]; kollision: string[] } {
  const eintraege: BuehnenEintrag[] = [];
  const aktId = aktuell ? (aktuell.bezug?.id ?? 'vorschau') : null;
  // Eine Nachfolgerin der angehefteten zaehlt als dieselbe (siehe `kette`).
  const hintergrundId = aktuell?.bezug?.schnellbuehne?.nachBuehneId ?? aktId;
  const dieselbe = gehoertZurAngehefteten(angeheftet, hintergrundId);
  if (angeheftet && !dieselbe) {
    eintraege.push({ id: angeheftet.id, doc: angeheftet.doc, bezug: angeheftet.bezug, sichtbar: false, angeheftet: true });
  }
  let kollision: string[] = [];
  if (aktuell && aktId) {
    kollision = angeheftet && !dieselbe ? gemeinsameFenster(angeheftet.doc, aktuell.doc) : [];
    if (angeheftet && dieselbe) {
      const veraltet = JSON.stringify(angeheftet.doc) !== JSON.stringify(aktuell.doc);
      // React-Schluessel bleibt die angeheftete Kennung: sonst baute die Nachfolgerin die Fenster neu.
      eintraege.push({ id: angeheftet.id, doc: angeheftet.doc, bezug: { ...angeheftet.bezug, schnellbuehne: aktuell.bezug?.schnellbuehne }, sichtbar: true, angeheftet: true, veraltet, zeigt: aktId });
    } else {
      eintraege.push({ id: hintergrundId ?? aktId, doc: aktuell.doc, bezug: aktuell.bezug, sichtbar: true, angeheftet: false, zeigt: aktId });
    }
  }
  return { eintraege, kollision };
}
